/*
 * Vremou videospeler (hero + videosectie)
 * ---------------------------------------------------------------
 * Gedrag:
 *   - Met data-autoplay start de video direct gedempt, in een loop.
 *   - Rechtsonder staat een kleine knop "Geluid aan"; die zet het
 *     geluid aan en schakelt de volledige videobediening in.
 *   - Blokkeert de browser autoplay (of staat 'beperk beweging' aan),
 *     dan verschijnt onmiddellijk een grote afspeelknop op de poster.
 *   - Klikken op de video pauzeert of hervat zolang de native
 *     bediening nog niet is ingeschakeld.
 *   - Buiten beeld pauzeert de video; terug in beeld hervat hij.
 * De poster verdwijnt zodra het afspelen daadwerkelijk begint.
 */

class VremouVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.poster = this.querySelector('[data-vr-video-poster]');
    this.playBtn = this.querySelector('[data-vr-video-play]');
    this.soundBtn = this.querySelector('[data-vr-sound]');
    this.soundLabel = this.querySelector('[data-vr-sound-label]');
    this.userPaused = false;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.wantsAutoplay = this.hasAttribute('data-autoplay') && !reduced;

    this.video.setAttribute('playsinline', '');
    this.video.controls = false;

    this.video.addEventListener('playing', () => this.onPlaying());
    this.video.addEventListener('pause', () => this.removeAttribute('data-playing'));

    if (this.playBtn) {
      this.playBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        this.manualStart();
      });
    }

    // De poster reageert alleen op de afspeelknop erin. Een klik op het
    // vlak zelf mag nooit het geluid aanzetten: dat gebeurt uitsluitend
    // via de knop "Geluid aan".
    if (this.poster) {
      this.poster.addEventListener('click', (event) => {
        if (!this.hasAttribute('data-blocked')) return;
        if (event.target.closest('[data-vr-video-play]')) return;
        this.manualStart();
      });
    }

    if (this.soundBtn) {
      this.soundBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleSound();
      });
    }

    this.video.addEventListener('click', (event) => {
      if (this.video.controls) return;
      event.preventDefault();
      this.togglePlay();
    });

    if (this.wantsAutoplay) {
      this.video.muted = true;
      this.attemptPlay();
      // Geen 'playing' binnen 1,5s? Dan is autoplay geblokkeerd:
      // toon meteen de grote afspeelknop.
      this.blockTimer = setTimeout(() => {
        if (this.video.paused) this.markBlocked();
      }, 1500);
    } else {
      this.video.removeAttribute('autoplay');
      if (!this.video.paused) this.video.pause();
      this.markBlocked();
    }

    this.observeVisibility();

    // Pauzeren wanneer het tabblad naar de achtergrond gaat, en weer
    // hervatten zodra het terugkomt. Zonder die tweede helft zou de
    // video na terugkeer stil blijven staan.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (!this.video.paused) this.video.pause();
      } else if (this.wantsAutoplay && !this.userPaused && this.video.paused) {
        this.attemptPlay();
      }
    });
  }

  attemptPlay() {
    const started = this.video.play();
    if (started && typeof started.catch === 'function') {
      started.catch(() => this.markBlocked());
    }
  }

  onPlaying() {
    if (this.blockTimer) clearTimeout(this.blockTimer);
    this.hasStarted = true;
    this.removeAttribute('data-blocked');
    this.setAttribute('data-playing', '');
    if (this.poster) this.poster.setAttribute('hidden', '');
    if (this.soundBtn && !this.video.controls) this.soundBtn.removeAttribute('hidden');
  }

  markBlocked() {
    this.setAttribute('data-blocked', '');
    if (this.poster) this.poster.removeAttribute('hidden');
  }

  /**
   * Expliciete start door de bezoeker nadat autoplay is geblokkeerd.
   * Geeft de volledige bediening, maar zet het geluid niet uit zichzelf aan.
   */
  manualStart() {
    this.userPaused = false;
    this.enableControls();
    if (this.poster) this.poster.setAttribute('hidden', '');
    this.attemptPlay();
  }

  togglePlay() {
    if (this.video.paused) {
      this.userPaused = false;
      this.attemptPlay();
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  }

  toggleSound() {
    this.video.muted = !this.video.muted;
    if (!this.video.muted) {
      // Geluid aan = de bezoeker kijkt echt: geef de volledige bediening.
      this.enableControls();
      if (this.video.paused) this.attemptPlay();
    }
    this.updateSoundLabel();
  }

  enableControls() {
    this.video.controls = true;
    // De native balk heeft een eigen volumeknop; de losse knop mag weg.
    if (this.soundBtn) this.soundBtn.setAttribute('hidden', '');
  }

  updateSoundLabel() {
    if (!this.soundBtn) return;
    const label = this.video.muted ? this.soundBtn.dataset.labelOff : this.soundBtn.dataset.labelOn;
    if (this.soundLabel && label) this.soundLabel.textContent = label;
    this.soundBtn.setAttribute('aria-pressed', this.video.muted ? 'false' : 'true');
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            // Alleen pauzeren wanneer de video ook echt heeft gespeeld.
            // De observer vuurt zijn eerste callback vóórdat de layout
            // klaar is; zonder deze grens zou dat het starten meteen
            // weer ongedaan maken.
            if (this.hasStarted && !this.video.paused) this.video.pause();
          } else if (this.wantsAutoplay && !this.userPaused && this.video.paused) {
            this.attemptPlay();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(this);
  }
}

if (!customElements.get('vremou-video')) {
  customElements.define('vremou-video', VremouVideo);
}
